import { Table, Thead, Tbody, Tr, Th, Td } from '@components/core-ui/Table/Table';
import { Divider } from '@components/core-ui/Divider';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { ASOneVideo } from '@modals/ASOneVideo';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { BillingAccountService, GetAllSubscriptionPlansPlanResponseDto, GetBillingAccountResponseDto } from 'app/API';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import DoneIcon from '@components/icons/DoneCheckmark.svg';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';

export const ChooseASOneSubscriptionModal = ({ onCancel, onSuccess, billingAccountId, filterPlans = [] }: TProps) => {
  const { call } = useAPI();
  const { updateMe } = useAuthContext();
  const navigate = useNavigate();
  const [isDesktopTutorialOpen, setIsDesktopTutorialOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [plans, setPlans] = useState<GetAllSubscriptionPlansPlanResponseDto[]>([]);
  const [billingAccount, setBillingAccount] = useState<GetBillingAccountResponseDto>();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setPlans([]);
      const [billingAccount, allPlans] = await Promise.all([
        call(BillingAccountService.findOne({ baId: billingAccountId })),
        call(BillingAccountService.getAsOnePlans()),
      ]);
      setBillingAccount(billingAccount);
      setPlans(allPlans.filter((plan) => !filterPlans?.includes(plan.id)));
      setIsLoading(false);
    })();
  }, []);

  const goToContactSales: React.MouseEventHandler<HTMLButtonElement> = (evt) => {
    evt.preventDefault();
    window.scroll(0, 0);
    navigate(routes.contactSales.make());
  };

  if (!billingAccount?.id) {
    return <></>;
  }

  const selectPlan = async (id: string) => {
    await updateMe();
    const plan = plans.find((item) => item.id === id);

    onSuccess({
      subscriptionProductId: id,
      planName: plan?.name ?? '',
      price: Number(plan?.price ?? 0),
      freeSeats: plan?.freeSeats || 0,
    });
  };

  const activeIds = (billingAccount?.subscriptions ?? [])
    .filter((sub) => {
      return sub.status === 'active' || sub.status === 'trialing';
    })
    .map((sub) => {
      return sub.productId;
    });
  const activePlans = plans.filter((plan) => {
    return activeIds.indexOf(plan.id) > -1;
  });
  const inactivePlans = plans
    .filter((plan) => {
      return activeIds.indexOf(plan.id) === -1;
    })
    .sort((a, b) => Number(a.price ?? 0) - Number(b.price ?? 0));

  if (isDesktopTutorialOpen) {
    return <ASOneVideo isOpen={true} onClose={() => setIsDesktopTutorialOpen(false)} />;
  }

  return (
    <ModalWrapper width="1260px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>AutoScriptOne Subscription Plans</ModalTitle>
      <Content>
        <h2 className="subtitle">
          <b>AutoScriptOne</b> is a cutting-edge Windows desktop application that revolutionizes court reporting by live
          streaming Speech-To-Text to digital reporters, stenographers and voice writers. This allows reporters to
          provide instant readback or playback, edit the transcript in real-time, and significantly reduce transcript
          production time and costs. Now with <b>AttorneyView</b> features, reporters can securely stream real-time text
          directly to attorneys and judges, enhancing collaboration in any reporting environment.
          <br />
          <br />
          <b>14-Day Free Trial:</b> Enjoy a full-featured free trial. Credit card required. Cancel your subscription
          anytime. (See{' '}
          <a target="_blank" rel="noreferrer" href="/agreement.html">
            Terms of Service
          </a>
          )
        </h2>

        <div className="scrollContainer">
          <Table className="table">
            <Thead>
              <Tr>
                <Th data-mobile-label="">
                  <span className="block mb-4">
                    <strong className="font-semibold">Monthly Subscription cost *</strong>
                  </span>
                </Th>
                {activePlans.map((plan) => {
                  const planSlug = plan.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown';
                  return (
                    <Th key={plan.id} data-mobile-label={plan.name}>
                      <div className="name text-center">{plan.name}</div>
                      <div className="price text-center">${Number(plan?.price ?? 0)}</div>
                      <div className="flex justify-center">
                        <Button disabled={true} onClick={() => {}} data-testid={`choose-plan-${planSlug}-current`}>
                          Current Plan
                        </Button>
                      </div>
                    </Th>
                  );
                })}
                {inactivePlans.map((plan) => {
                  const planSlug = plan.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown';
                  return (
                    <Th key={plan.id} data-mobile-label={plan.name}>
                      <div className="name text-center">{plan.name}</div>
                      <div className="price text-center">${Number(plan?.price ?? 0)}</div>
                      <div className="flex justify-center">
                        <Button onClick={() => selectPlan(plan.id)} data-testid={`choose-plan-${planSlug}`}>
                          Choose Plan
                        </Button>
                      </div>
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">High Quality Audio Capture</strong>
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  return (
                    <Td key={plan.id}>
                      <strong className="font-semibold">
                        <small>Unlimited</small>
                      </strong>
                    </Td>
                  );
                })}
              </Tr>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">Reporter Streaming Hours Per Month</strong> **
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  return (
                    <Td key={plan.id}>
                      {plan.unlimitedHoursPerMonth === true ? (
                        <strong className="font-semibold">
                          <small>Unlimited</small>
                        </strong>
                      ) : (
                        plan.audioHoursPerMonth || (
                          <strong className="font-semibold">
                            <small>Pay per use</small>
                          </strong>
                        )
                      )}
                    </Td>
                  );
                })}
              </Tr>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">Reporter Streaming Per Hour</strong>
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  const price = Number(plan.DefaultBillingConfiguration?.realTimeRate);
                  const free = !price && plan.DefaultBillingConfiguration?.realTimeRateType === 'FLAT_FEE';
                  // const perHour = ['PER_HOUR', 'TIER_RATE'].includes(
                  //   plan.DefaultBillingConfiguration?.realTimeRateType as string,
                  // );
                  const payPerUse = !free && !price;
                  return (
                    <Td key={plan.id}>
                      {free ? (
                        <strong className="font-semibold">
                          <small>Free</small>
                        </strong>
                      ) : payPerUse ? (
                        <strong className="font-semibold">
                          <small>Pay per use</small>
                        </strong>
                      ) : (
                        `$${price}`
                      )}
                    </Td>
                  );
                })}
              </Tr>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">AI Reporter Notes</strong>
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  return (
                    <Td key={plan.id}>
                      <DoneIcon width="30px" />
                    </Td>
                  );
                })}
              </Tr>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">Zoom Audio Capture</strong>
                  <br /> (Multi-channel integration)
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  return (
                    <Td key={plan.id}>
                      <DoneIcon width="30px" />
                    </Td>
                  );
                })}
              </Tr>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">Remote Audio Capture:</strong>
                  <br /> (Teams, Webex, Google Meet, GoToMeeting)
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  return (
                    <Td key={plan.id}>
                      <DoneIcon width="30px" />
                    </Td>
                  );
                })}
              </Tr>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">In-person Capture with Streaming</strong>
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  return (
                    <Td className="in-person-channels" key={plan.id}>
                      <DoneIcon width="30px" />
                    </Td>
                  );
                })}
              </Tr>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">AI Transcript Unlock Per Hour</strong>
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  const price = Number(plan.DefaultBillingConfiguration?.fullUnlockRate);
                  const free = !price;
                  return (
                    <Td key={plan.id}>
                      {free ? (
                        <strong className="font-semibold">
                          <small>Free</small>
                        </strong>
                      ) : (
                        `$${price}`
                      )}
                    </Td>
                  );
                })}
              </Tr>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">
                    AI-Enhanced Transcript Per Hour
                    <br />
                    High Accuracy Rough (Post-Session)
                  </strong>
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  return (
                    <Td key={plan.id}>
                      {plan.allowedToChooseTranscriptUnlockType ? (
                        <DoneIcon width="30px" />
                      ) : Number(plan.DefaultBillingConfiguration?.enhancedOnDemandRate) ? (
                        `$${plan.DefaultBillingConfiguration?.enhancedOnDemandRate}`
                      ) : (
                        <strong className="font-semibold">
                          <small>Pay per use</small>
                        </strong>
                      )}
                    </Td>
                  );
                })}
              </Tr>
              <Tr>
                <Td className="text-medium">
                  <strong className="font-semibold">
                    AttorneyView
                    <br />
                    (Realtime Streaming Text)
                  </strong>
                </Td>
                {[...activePlans, ...inactivePlans].map((plan) => {
                  return (
                    <Td key={plan.id}>
                      <strong className="font-semibold">
                        <small>Pay per use</small>
                      </strong>
                    </Td>
                  );
                })}
              </Tr>
            </Tbody>
          </Table>
        </div>

        <div className="fair-use">
          * Plus applicable sales tax
          <br />
          ** Fair Use Overage Charges May Apply - See{' '}
          <a target="_blank" rel="noreferrer" href="/agreement.html">
            Terms of Service
          </a>
        </div>
        <Divider className="divider" />

        <div className="more-info flex items-center h-[40px]">
          Discounts are available for annual contracts. For high-volume enterprise plans, please contact our sales team{' '}
          <div className="ml-4 w-40">
            <OutlinedButton onClick={goToContactSales}>Contact Our Sales Team</OutlinedButton>
          </div>
        </div>
        <div className="our-fair-usage-policy">
          Additional Fair Use Charges May Apply. For details, please review our Terms of Service.
        </div>
        <Divider className="divider" />
        <div className="more-info">
          <b>Need More Information?</b> Contact our product specialist team for a demonstration or to discuss custom,
          multi-user pricing options.
        </div>

        <div className="button-container">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <div className="right">
            <OutlinedButton className="green-button" onClick={() => setIsDesktopTutorialOpen(true)}>
              Watch Intro Video
            </OutlinedButton>
            <BoldButton onClick={goToContactSales}>Contact Our Sales Team</BoldButton>
          </div>
        </div>
      </Content>
    </ModalWrapper>
  );
};
